import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SOCIAL_AUTH_PROVIDER_GOOGLE } from '@common/constants/SocialAuthProvider';
import { socialAuth } from '@core/actions/UserActions';

// Shared availability check — callers that render a sibling "or"
// divider next to the Google button need to match whatever the button
// itself decides to render. Reading Redux in two places keeps it
// single-sourced so the divider can't appear without the button.
export function useGoogleAuthAvailable() {
    const { socialAuthStatusGoogle, googleAppId } = useSelector(state => state.site);
    return Boolean(socialAuthStatusGoogle && googleAppId);
}

// Port of v5's GoogleSignInButton tuned to the tutoriage-style `.social-button--google`
// markup: the Google JS SDK renders an off-screen real Google button and we trigger it
// via a visible button that matches the prod look (logo + label, grey border, white bg).
export default function GoogleSignInButton({ label, onSuccess, className, withDelimiter = false }) {
    const dispatch = useDispatch();
    const { socialAuthStatusGoogle, googleAppId } = useSelector(state => state.site);

    useEffect(() => {
        if (!socialAuthStatusGoogle || !googleAppId) {
            return undefined;
        }

        const parseJwt = (token) => {
            const base64Url = token.split('.')[1];
            const base64 = decodeURIComponent(
                atob(base64Url)
                    .split('')
                    .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                    .join(''),
            );
            return JSON.parse(base64);
        };

        const callback = (authResponse) => {
            const profile = parseJwt(authResponse.credential);
            const authData = {
                provider: SOCIAL_AUTH_PROVIDER_GOOGLE,
                // GIS callback shape: `{credential: <JWT id_token>, clientId: '<oauth-client-id>', ...}`.
                // Backend `SocialAuthForm::customValidation` hits Google's
                // `tokeninfo?id_token=` endpoint with whatever lands in
                // `access_token`; sending the bare `clientId` (the OAuth
                // app id, NOT a JWT) produced 400 from tokeninfo and a
                // 422 "Invalid Google credential" on the customer side.
                // Use the JWT id_token (`credential`) — that's what
                // tokeninfo actually validates.
                access_token: authResponse.credential,
                name: profile.name,
                social_id: profile.sub,
                email: profile.email,
                user_time_offset: new Date().getTimezoneOffset() * -1,
                additional_data: { profile, authResponse },
            };

            dispatch(socialAuth(authData)).then(() => {
                if (onSuccess) {
                    onSuccess();
                } else {
                    window.location.href = '/account/';
                }
            });
        };

        // GIS opens its OAuth popup via `window.open` with hard-coded
        // left/top features computed from where its hidden anchor div
        // happens to sit in the DOM — which puts the popup wherever
        // our offscreen wrapper (`display: none; position: ...`)
        // landed (typically far off-centre). Wrap `window.open` for
        // any accounts.google.com URL so we recompute centred geometry
        // before delegating. Mirrors the v5 GoogleSignInButton patch.
        const _winOpen = window.open.bind(window);
        const patchedOpen = function (url, name, features) {
            if (typeof url === 'string' && url.includes('accounts.google.com') && typeof features === 'string') {
                const w = 500;
                const h = 600;
                const l = Math.round(window.screenX + (window.outerWidth - w) / 2);
                const t = Math.round(window.screenY + (window.outerHeight - h) / 2);
                features = features
                    .replace(/left=[-\d]+/i, 'left=' + l)
                    .replace(/top=[-\d]+/i, 'top=' + t);
            }
            return _winOpen(url, name, features);
        };
        window.open = patchedOpen;

        const initGoogleSignIn = () => {
            if (!window.google?.accounts?.id) return;
            window.google.accounts.id.initialize({
                client_id: googleAppId,
                ux_mode: 'popup',
                callback,
            });

            const wrapper = document.createElement('div');
            wrapper.style.display = 'none';
            wrapper.className = 'bidding-custom-google-button';
            document.body.appendChild(wrapper);

            window.google.accounts.id.renderButton(wrapper, { type: 'icon' });
            const realBtn = wrapper.querySelector('div[role=button]');

            window.handleGoogleLogin = () => realBtn && realBtn.click();
        };

        if (window.google?.accounts?.id) {
            initGoogleSignIn();
        } else {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = initGoogleSignIn;
            document.body.appendChild(script);
        }

        return () => {
            delete window.handleGoogleLogin;
            // Restore the original `window.open` on unmount so we don't
            // leak the patched version into other surfaces (e.g. modals
            // that re-mount this component would otherwise stack
            // multiple wrappers).
            window.open = _winOpen;
        };
    }, [dispatch, googleAppId, socialAuthStatusGoogle, onSuccess]);

    if (!socialAuthStatusGoogle || !googleAppId) {
        return null;
    }

    // `className` is caller-supplied (v2 auth uses `.bidding-auth2-google`).
    // `withDelimiter` reproduces the legacy "or" bar above the button for
    // any remaining callsites that rely on the old modal chrome (e.g.
    // register / reset inside the legacy `.bidding-order-modal`).
    const btnClass = className || 'bidding-social-button--google';

    return (
        <>
            {withDelimiter && (
                <span className="bidding-login-modal__delimiter">or</span>
            )}
            <button
                type="button"
                className={btnClass}
                onClick={() => window.handleGoogleLogin && window.handleGoogleLogin()}
            >
                <svg viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#ea4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.6 2.1 30.1 0 24 0 14.7 0 6.8 5.3 3 13l7.9 6.1C12.8 13.6 17.9 9.5 24 9.5z"/>
                    <path fill="#4285f4" d="M46.5 24.5c0-1.7-.2-3.3-.5-4.9H24v9.3h12.7c-.6 3-2.3 5.6-4.9 7.3l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.6z"/>
                    <path fill="#fbbc04" d="M10.9 28.1A14.5 14.5 0 0 1 10.1 24c0-1.4.3-2.8.8-4.1L3 13.8A24 24 0 0 0 0 24c0 3.9.9 7.6 2.6 10.9l8.3-6.8z"/>
                    <path fill="#34a853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.1 0-11.2-4.1-13.1-9.6L3 34.9C6.8 42.7 14.7 48 24 48z"/>
                </svg>
                {label}
            </button>
        </>
    );
}
